import type {
  ProductCollectionFilter,
  ProductCollectionRangeGroupFilter,
  ProductCollectionTermFilter,
  ProductCollectionTermsFilter,
} from 'src/types';
import type { RouteState } from '../product-collection';
import queryString from 'query-string-for-all';
import { extractFiltersFromUrl } from '@prisjakt/product-collection-urls';
import { updateProductCollectionFilters } from '../actions';
import { getSortKeyForProperty } from '../utils/properties';

// We need to do this here because we need access to the store in updateFilters
// However we can't statically import it since produces with SSR.
// This way, we only import it in the browser.
let store: any;
if (IS_BROWSER) {
  // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
  store = require('src/client/store').default;
}

const hasOnlyValidFilters = (filter: ProductCollectionFilter) => {
  if (!('type' in filter)) {
    return false;
  }

  switch (filter.type) {
    case 'term':
      return 'id' in filter && filter.id;
    case 'terms':
      return 'ids' in filter && filter.ids && filter.ids.length;
    default:
      return true;
  }
};

const replaceOrAdd = <T extends { [key: string]: any }>(
  array: T[],
  entry: T,
  matchBy: keyof T = 'property'
) => {
  const index = array.findIndex(x => x[matchBy] === entry[matchBy]);
  const newArr = [...array];
  if (index >= 0) {
    newArr.splice(index, 1, entry);
  } else {
    newArr.push(entry);
  }

  return newArr;
};

const updateFilters = (
  state: RouteState,
  filters: ProductCollectionFilter[]
) => {
  const newState = {
    ...state,
    // Reset offset whenever filters are updated
    url: undefined,
    offset: 0,
    filters,
  };

  if (state.id) {
    const filterAction = updateProductCollectionFilters(state.id, filters);
    store.dispatch(filterAction);
  }

  return newState;
};

// TODO: Update types once resolvers are properly typed (moved to TS)
export const productCollectionStaticReducer = (
  state: RouteState,
  action: any
) => {
  switch (action.type) {
    case 'URL_CHANGE': {
      const { offset, sort, direction } = queryString.parse(
        action.location.search
      );
      return {
        ...state,
        url: undefined,
        offset: typeof offset === 'string' ? parseInt(offset, 10) : 0,
        sort,
        direction,
        filters: extractFiltersFromUrl(
          `${action.location.pathname}${action.location.search}`
        ).map(f => {
          // eslint-disable-next-line no-param-reassign
          delete f?.operator; // ProductCollectionFilterInput type doesn't allow operator property
          return f;
        }),
      };
    }
    // BUG: ??? currently in production when we change sorting, the URL resets itself ???
    case 'SORT_CHANGE': {
      const { offset, direction } = queryString.parse(action.location.search);
      return {
        ...state,
        sort: action.sort,
        offset: typeof offset === 'string' ? parseInt(offset, 10) : 0,
        direction,
      };
    }
    case 'PAGINATION': {
      const { sort, direction } = queryString.parse(action.location.search);
      return {
        ...state,
        offset: action.clickedOffset,
        sort,
        direction,
      };
    }
    case 'FILTER_RANGE_UPDATE': {
      return updateFilters(state, replaceOrAdd(state.filters, action.filter));
    }
    case 'FILTER_RANGE_GROUP_UPDATE': {
      let key;
      if (action.multiple) {
        const currentFilter = state.filters.find(
          f => 'property' in f && f.property === action.property
        ) as ProductCollectionRangeGroupFilter | undefined;

        let currentKeys: ProductCollectionRangeGroupFilter['key'] = [];

        if (currentFilter) {
          currentKeys = Array.isArray(currentFilter.key)
            ? currentFilter.key
            : [currentFilter.key];
        }

        const index = currentKeys.indexOf(action.id);
        if (index >= 0) {
          currentKeys.splice(index, 1);
        } else {
          currentKeys.push(action.id);
        }
        key = currentKeys;
      } else {
        key = [action.id];
      }
      const filter = {
        type: 'range-group',
        property: action.property,
        key,
      };

      return updateFilters(
        state,
        !filter.key.length
          ? state.filters.filter(
              f => 'property' in f && f.property !== action.property
            )
          : replaceOrAdd(state.filters, filter)
      );
    }
    case 'FILTER_BOOLEAN_UPDATE': {
      const filter = {
        type: 'boolean',
        id: action.id,
        property: action.property,
      };
      const newFilters = replaceOrAdd(state.filters, filter);
      return updateFilters(state, newFilters.filter(hasOnlyValidFilters));
    }
    case 'FILTER_LIST_UPDATE': {
      const { id, clearCurrentFilters } = action;
      const filters = clearCurrentFilters ? [] : state.filters;
      const currentFilter = filters.find(
        f => 'property' in f && f.property === action.property
      ) as
        | ProductCollectionTermsFilter
        | ProductCollectionTermFilter
        | undefined;

      const termId =
        currentFilter && 'id' in currentFilter ? currentFilter.id : [];
      const termIds =
        currentFilter && 'ids' in currentFilter ? currentFilter.ids : [];

      const ids = termIds.concat(termId);

      if (!id) {
        return state;
      }

      if (!action.multiple) {
        const newFilters = replaceOrAdd(filters, {
          type: 'term',
          id,
          property: action.property,
        });

        return updateFilters(state, newFilters.filter(hasOnlyValidFilters));
      }

      const index = ids.indexOf(id);
      if (index >= 0) {
        ids.splice(index, 1);
      } else {
        ids.push(id);
      }

      const filter =
        ids.length === 1
          ? {
              type: 'term',
              property: action.property,
              id: ids[0],
            }
          : {
              type: 'terms',
              property: action.property,
              ids,
            };

      const newFilters = replaceOrAdd(filters, filter);

      return updateFilters(state, newFilters.filter(hasOnlyValidFilters));
    }
    case 'FILTER_LIST_TREE_UPDATE': {
      const otherActiveFilters = state.filters.filter(
        f => 'property' in f && f.property !== action.property
      );

      const ids = action.ids;

      const filter =
        ids.length === 1
          ? {
              type: 'term',
              property: action.property,
              id: ids[0],
            }
          : {
              type: 'terms',
              property: action.property,
              ids,
            };

      const filters = ids.length
        ? [...otherActiveFilters, filter]
        : otherActiveFilters;
      return updateFilters(state, filters);
    }
    case 'FILTER_NAME_SEARCH_UPDATE': {
      const newFilters = state.filters.filter(
        f => 'type' in f && f.type !== 'name-search'
      );
      if (action.query?.length) {
        newFilters.push({
          type: 'name-search',
          query: action.query,
        });
      }
      return updateFilters(state, newFilters.filter(hasOnlyValidFilters));
    }
    case 'FILTER_REMOVE': {
      // If a property is passed, clear filters for that property, otherwise all filters
      return action.property
        ? updateFilters(
            state,
            state.filters.filter(
              f => 'property' in f && f.property !== action.property
            )
          )
        : updateFilters(state, []);
    }
    case 'ADD_PROPERTY_COLUMN': {
      return {
        ...state,
        propertyColumnIds: state.propertyColumnIds.concat([action.propertyId]),
      };
    }
    case 'REMOVE_PROPERTY_COLUMN': {
      const propertyColumnIds = state.propertyColumnIds.filter(
        property => property !== action.propertyId
      );

      // If we're currently sorting on the column we're removing, clear the sort method as well
      if (state.sort === getSortKeyForProperty(action.propertyId)) {
        return { ...state, propertyColumnIds, sort: undefined };
      }

      return { ...state, propertyColumnIds };
    }
    default: {
      return state;
    }
  }
};
